import React from 'react'
import type { ButtonOption } from '../types'
import styles from './ButtonInput.module.css'

export interface ButtonInputProps {
  /**
   * Array of button options for user to choose from
   */
  options: ButtonOption[]

  /**
   * Callback when user selects an option
   */
  onSelect: (value: string) => void

  /**
   * Whether buttons are disabled
   * @default false
   */
  disabled?: boolean

  /**
   * Primary brand color for button styling
   * @default '#0066CC'
   */
  primaryColor?: string
}

/**
 * Button input component for user choices
 * Renders touch-friendly buttons with optional emojis
 */
export const ButtonInput: React.FC<ButtonInputProps> = ({
  options,
  onSelect,
  disabled = false,
  primaryColor = '#0066CC',
}) => {
  const handleClick = (value: string) => {
    if (!disabled) {
      onSelect(value)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent, value: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClick(value)
    }
  }

  return (
    <div className={styles.buttonInput} data-testid="button-input">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={styles.optionButton}
          onClick={() => handleClick(option.value)}
          onKeyDown={(e) => handleKeyDown(e, option.value)}
          disabled={disabled}
          data-value={option.value}
          data-testid="option-button"
          style={
            !disabled
              ? {
                  borderColor: primaryColor,
                  '--hover-bg': `${primaryColor}10`,
                  '--hover-border': primaryColor,
                } as React.CSSProperties
              : undefined
          }
        >
          {option.emoji && <span className={styles.emoji}>{option.emoji}</span>}
          <span className={styles.label}>{option.label}</span>
        </button>
      ))}
    </div>
  )
}
